// Globals
import React, { useCallback, useMemo, useState } from 'react';

// UI
import { Button, ConfigProvider, Flex, message, Modal, Tooltip } from 'antd';
import {
  FacebookShareButton,
  TwitterShareButton,
  LinkedinShareButton,
} from 'react-share';

import QRCode from 'qrcode.react';

import {
  CopyOutlined,
  DownloadOutlined,
  ExportOutlined,
  FacebookFilled,
  LinkedinFilled,
} from '@ant-design/icons';

// Locals
import publishedSuccess from '../../studio/assets/newBrandingIllustrations/send_icon_red.png';
import styles from './GiideWithStep.module.css';
import useGiideOperations from '../../hooks/useGiideOperations';
import { getCanvasElementById } from '../utils';
import { EmbedCode } from './EmbedCode';
import useExportVideo from '../../hooks/useSubscription/useExportVideo';
import { tealButtonTheme } from '../../theme';

const QRCodeIcon = () => {
  return (
    <svg width="32" height="32" viewBox="0 0 16 16">
      <g
        transform="scale(.5,.5)"
        fill="none"
        stroke="#000"
        strokeWidth="2"
        strokeLinecap="butt"
      >
        <rect x="0" y="0" width="300" height="422" stroke="none" />
        <path id="A" d="M3,3 h8 v8 h-8 v-8 z M6,7 h2" />
        <use xlinkHref="#A" x="18" y="0" />
        <use xlinkHref="#A" x="0" y="18" />
        <path d="M18,3 h-3 v4 h2 v4 h-2 v3" />
        <path d="M2,15 h2 m2,0 h4 m6,0 h2 m2,0 h4 m4,0 h2" />
        <path d="M4,17 h2 m2,0 h8 m6,0 h2 m2,0 h4" />
        <path d="M14,19 h2 m2,0 h2 m6,0 h2" />
        <path d="M16,21 h2 m2,0 h8" />
        <path d="M14,23 h2 m2,0 h2 m4,0 h2 m2,0 h2" />
        <path d="M14,25 h4 m2,0 h2 m2, 0 h4" />
        <path d="M14,27 h2 m6,0 h2 m4,0 h2" />
        <path d="M16,29 h8 m2,0 h2" />
      </g>
    </svg>
  );
};

const XLogoIcon = () => {
  return (
    <svg
      width="32"
      height="28"
      viewBox="0 0 1200 1227"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M714.163 519.284L1160.89 0H1055.03L667.137 450.887L357.328 0H0L468.492 681.821L0 1226.37H105.866L515.491 750.218L842.672 1226.37H1200L714.137 519.284H714.163ZM569.165 687.828L521.697 619.934L144.011 79.6944H306.615L611.412 515.685L658.88 583.579L1055.08 1150.3H892.476L569.165 687.854V687.828Z"
        fill="black"
      />
    </svg>
  );
};

interface IProps {
  giideId: string;
  giideTitle: string;
  giideAuthor?: string | null;
  giideEmbedCodeState?: string | null;
  giideEmbedCodeAllowedDomains?: (string | null)[] | null;
  exportState?: string | null;
}

const StepPublished: React.FC<IProps> = ({
  giideId,
  giideTitle,
  giideAuthor,
  giideEmbedCodeState,
  giideEmbedCodeAllowedDomains,
}) => {
  const [QRCodeModalVisible, setQRCodeModalVisible] = useState<boolean>(false);
  const { createNewGiide, giideSaving } = useGiideOperations();
  const { handleOpenExportModal } = useExportVideo();
  const [messageAPI, contextHolder] = message.useMessage();

  const giideUrl = useMemo<string>(
    () => `https://${process.env.REACT_APP_PLAYER_URL}/giide/${giideId}`,
    [giideId]
  );

  const playerUrl = useMemo<string>(
    () =>
      `https://${process.env.REACT_APP_EMBED_URL}/giide/v2/giide-${giideId}`,
    [giideId]
  );

  const copyToClipboard = useCallback(() => {
    navigator.clipboard
      .writeText(giideUrl)
      .then(() => messageAPI.success('Giide URL copied to clipboard'));
  }, [giideUrl, messageAPI]);

  const handleCreateGiide = useCallback(async () => {
    await createNewGiide();
  }, [createNewGiide]);

  const onImageDownload = useCallback(() => {
    const canvas = getCanvasElementById('QRCode');
    if (canvas) {
      const dataUrl = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.download = 'QRCode.png';
      downloadLink.href = dataUrl;
      downloadLink.click();
    }
  }, []);

  const handleClickExportButton = () => {
    handleOpenExportModal(giideId, giideTitle);
  };

  return (
    <ConfigProvider>
      {contextHolder}
      <div className={styles.publishedWrapper}>
        <Modal
          title={'QR Code'}
          open={QRCodeModalVisible}
          onCancel={() => setQRCodeModalVisible(false)}
          footer={null}
          width={'fit-content'}
        >
          <Flex vertical={true} gap={'middle'}>
            <div className={styles.shareQRCodeHeader}>
              <div>
                <b>{giideTitle}</b>
              </div>
              <div>{giideAuthor ?? 'Author name'}</div>
            </div>
            <QRCode
              id="QRCode"
              className={styles.shareQRCode}
              includeMargin={true}
              size={191}
              level={'L'}
              value={giideUrl}
            />
            <Button
              style={{ color: 'white' }}
              type={'primary'}
              onClick={onImageDownload}
              icon={<DownloadOutlined />}
            >
              Download
            </Button>
          </Flex>
        </Modal>
        <div className={styles.successPageLeft}>
          <iframe src={playerUrl} title="giide player" />
        </div>

        <div className={styles.successPageRight}>
          <h2>Success! Your giide is now published</h2>
          <p>
            Get it out into the world by sharing the link below via email, text,
            or any of your favorite social channels.
          </p>
          <img
            className={styles.publishedSuccessImg}
            src={publishedSuccess}
            alt="published success"
          />
          <div className={styles.publishedUrlWrapper}>
            <div className={styles.giideListenUrl}>{giideUrl}</div>
            <ConfigProvider theme={tealButtonTheme}>
              <Tooltip placement="top" title={'Copy giide URL'}>
                <Button
                  type={'primary'}
                  size={'large'}
                  shape={'circle'}
                  onClick={copyToClipboard}
                  icon={<CopyOutlined />}
                ></Button>
              </Tooltip>
            </ConfigProvider>
          </div>
          <Flex
            gap={'middle'}
            justify={'center'}
            className={styles.publishedShareIcons}
          >
            <Tooltip title={'Share on LinkedIn'} placement={'top'}>
              <LinkedinShareButton url={giideUrl}>
                <LinkedinFilled style={{ fontSize: 32 }} />
              </LinkedinShareButton>
            </Tooltip>
            <Tooltip title={'Share on Facebook'} placement={'top'}>
              <FacebookShareButton url={giideUrl}>
                <FacebookFilled style={{ fontSize: 32 }} />
              </FacebookShareButton>
            </Tooltip>
            <Tooltip title={'Share on X (Twitter)'} placement={'top'}>
              <TwitterShareButton
                url={giideUrl}
                className={styles.customIconAlign}
              >
                <XLogoIcon />
              </TwitterShareButton>
            </Tooltip>
            <Tooltip title={'Get QR Code'} placement={'top'}>
              <span
                className={styles.shareQRCodeWrapper}
                onClick={() => setQRCodeModalVisible(true)}
              >
                <QRCodeIcon />
              </span>
            </Tooltip>
            <EmbedCode
              giideId={giideId}
              giideEmbedCodeState={giideEmbedCodeState}
              giideEmbedCodeAllowedDomains={giideEmbedCodeAllowedDomains}
            />

            <Tooltip title={'Export to Video'} placement={'top'}>
              <ExportOutlined
                onClick={handleClickExportButton}
                style={{ fontSize: 32 }}
              />
            </Tooltip>
          </Flex>
          <div>
            <Button
              className={styles.startNewGiide}
              type={'primary'}
              onClick={handleCreateGiide}
              loading={giideSaving}
              disabled={giideSaving}
            >
              Start Another Giide
            </Button>
          </div>
        </div>
      </div>
    </ConfigProvider>
  );
};

export default StepPublished;
